import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { Upload, Trash2, Loader2, ImageOff } from "lucide-react";

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [enabled, setEnabled] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    api.get("/admin/gallery").then(({ data }) => setItems(data)).catch(() => {});
    api.get("/admin/settings").then(({ data }) => setEnabled(data.gallery_enabled !== false)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post("/admin/gallery", fd);
      }
      toast.success(files.length > 1 ? `${files.length} photos ajoutées` : "Photo ajoutée");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de l'envoi");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (id) => {
    await api.delete(`/admin/gallery/${id}`);
    toast.success("Photo supprimée");
    load();
  };

  const toggle = async (val) => {
    setEnabled(val);
    const { data: s } = await api.get("/admin/settings");
    await api.put("/admin/settings", { ...s, gallery_enabled: val });
    toast.success(val ? "Galerie affichée sur le site" : "Galerie masquée");
  };

  return (
    <div className="max-w-4xl">
      <h1 className="font-serif text-3xl font-bold text-brand-green mb-2">Galerie photos</h1>
      <p className="text-brand-green/70 mb-6">Ajoutez les vraies photos de vos sets. Elles s'affichent sur la page d'accueil dans la section « Photos réelles de nos sets ».</p>

      <div className="bg-white rounded-xl border border-brand-green/10 p-5 mb-6 flex items-center justify-between flex-wrap gap-4">
        <label className="flex items-center gap-3 text-brand-green font-medium">
          <input type="checkbox" data-testid="gallery-toggle" checked={enabled} onChange={(e) => toggle(e.target.checked)} className="w-5 h-5 accent-brand-green" />
          Afficher la section galerie sur le site
        </label>
        <div>
          <input ref={fileRef} type="file" accept="image/*" multiple onChange={onFiles} className="hidden" data-testid="gallery-file-input" />
          <button data-testid="gallery-upload-btn" disabled={uploading} onClick={() => fileRef.current?.click()}
            className={`inline-flex items-center gap-2 rounded-full px-6 py-3 font-bold ${uploading ? "bg-gray-200 text-gray-400" : "bg-brand-gold text-brand-green hover:bg-brand-goldHover"}`}>
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploading ? "Envoi…" : "Ajouter des photos"}
          </button>
        </div>
      </div>

      {!enabled && items.length > 0 && (
        <p className="text-sm text-brand-alert mb-4">La galerie est masquée : ces photos ne sont pas visibles sur le site.</p>
      )}

      {items.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-brand-green/25 p-12 text-center text-brand-green/50" data-testid="gallery-empty">
          <ImageOff className="w-10 h-10 mx-auto mb-3" />
          Aucune photo pour le moment. La section n'apparaît pas sur le site tant qu'aucune photo n'est ajoutée.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="gallery-grid">
          {items.map((it) => (
            <div key={it.id} className="relative group aspect-square rounded-2xl overflow-hidden border border-brand-green/15 bg-brand-sage" data-testid={`gallery-item-${it.id}`}>
              <img src={`${API}/gallery/${it.id}/image`} alt={it.original_filename || "Photo set"} loading="lazy" className="w-full h-full object-cover" />
              <button data-testid={`gallery-delete-${it.id}`} onClick={() => remove(it.id)}
                className="absolute top-2 right-2 w-9 h-9 rounded-full bg-white/90 text-brand-alert flex items-center justify-center shadow hover:bg-white opacity-0 group-hover:opacity-100 transition-opacity">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
